import { ItemWarehouse } from "./api/itemwarehouse"
import {ItemWarehousePlanningDetails} from "./itemwarehouseplanningdetails"
import {ItemCode, WarehouseCode} from "./api/types"
import {ISODate} from "./date"
import {Tuple, tuple} from "./tuple"
import {NEVER} from "./constants"
import utils from "./utils"


type ProjectedStockOnDay = {
  date: ISODate
  iwpds: readonly ItemWarehousePlanningDetails[]
  eodProjectedRealStock: number
  eodProjectedAvailableStock: number
}

export type ProjectedStock = {
  itemCode: ItemCode
  warehouseCode: WarehouseCode
  currentAvailableStock: number
  currentRealStock: number
  projection: Map<ISODate, ProjectedStockOnDay>
}

export function calculateProjectedStocks(
  itemWarehouses: readonly ItemWarehouse[],
  itemWarehousePlanningDetails_s: readonly ItemWarehousePlanningDetails[],
): readonly ProjectedStock[] {

  const itemWarehouseLookup = new Map<Tuple<[ItemCode, WarehouseCode]>, ItemWarehouse>(
    itemWarehouses.map(iw => [tuple([iw.itemCode, iw.warehouseCode]), iw]))
  const iwpdsLookup = utils.intoBuckets(
    itemWarehousePlanningDetails_s.filter(iwpd => iwpd.plannedDate.lt(NEVER)),
    (iwpd): Tuple<[ItemCode, WarehouseCode]> =>
      tuple([iwpd.itemCode, iwpd.warehouseCode]))

  const allKeys = [...new Set([...itemWarehouseLookup.keys(), ...iwpdsLookup.keys()])]
  const projectedAvailableStocks: ProjectedStock[] = allKeys.map(key => {
    const [itemCode, warehouseCode] = key
    const currentStock = itemWarehouseLookup.get(key)?.currentStock ?? 0

    return [...iwpdsLookup.get(key) ?? []]
      .sort((a, b) => ISODate.compare(a.plannedDate, b.plannedDate))
      .reduce((pas, iwpd) => {
        const key = iwpd.plannedDate
        if (!pas.projection.has(key)) {
          pas.projection.set(key, {
            date: iwpd.plannedDate,
            iwpds: [],
            eodProjectedRealStock: NaN,
            eodProjectedAvailableStock: NaN})
        }
        const item = pas.projection.get(key)!
        item.iwpds = [...item.iwpds, iwpd]
        return pas
      }, {
        currentRealStock: currentStock,
        currentAvailableStock: NaN,
        projection: new Map(),
        itemCode: itemCode,
        warehouseCode: warehouseCode,
      } as ProjectedStock)
  })

  // now forward iterate to fix real stock
  projectedAvailableStocks.forEach(projectedAvailableStock => {
    let lastStock = projectedAvailableStock.currentRealStock
    projectedAvailableStock.projection.forEach((ps) => {
      const change = ps.iwpds.reduce((sum, iwpd) => sum + iwpd.plannedQuantity, 0)
      lastStock += change
      ps.eodProjectedRealStock = lastStock
    })
  })

  // backward iterate to get the available stock (always the min of all future RS
  projectedAvailableStocks.forEach(projectedAvailableStock => {
    let lowestStock = Infinity
    Array.from(projectedAvailableStock.projection.values()).reverse().forEach(
      (ps) => {
        lowestStock = Math.min(lowestStock, ps.eodProjectedRealStock)
        ps.eodProjectedAvailableStock = lowestStock
      }
    )
    projectedAvailableStock.currentAvailableStock = Math.min(
      lowestStock, projectedAvailableStock.currentRealStock)
  })
  return projectedAvailableStocks
}
